import type { IsoDate } from './calendar';
import { assertPaise, type Paise } from './money';

/**
 * Tax on a sale, and where it goes in GSTR-1.
 *
 * Tax is worked out per line (each line has one rate): IGST when the supply is inter-state, else CGST
 * and SGST (or UTGST, reported the same way) at half the rate each, each rounded to the paisa on its
 * own. A registered buyer's sale is B2B (table 4A). A consumer's sale is "B2C large" (table 5) when it's
 * inter-state and the invoice is worth more than ₹1 lakh (₹2.5 lakh before August 2024); every other
 * consumer sale is "B2C others" (table 7), reported in totals by rate and place of supply.
 */

/** A GST rate in percent, e.g. 18, 5, 0.25. */
export type Rate = number;

/** A rate in basis points (18% → 1800), so tax is worked out in whole numbers. */
export function rateBasisPoints(rate: Rate): number {
  const bp = Math.round(rate * 100);
  if (!Number.isFinite(rate) || rate < 0 || Math.abs(bp - rate * 100) > 1e-6) {
    throw new RangeError(`Not a GST rate: ${rate}`);
  }
  return bp;
}

/** amount × bp / divisor, rounded half up to the paisa (amounts are never negative here). */
function share(amount: Paise, bp: number, divisor: number): Paise {
  const exact = amount * bp;
  if (!Number.isSafeInteger(exact)) throw new RangeError('amount too large');
  return Math.floor((exact * 2 + divisor) / (divisor * 2));
}

export interface LineTax {
  igst: Paise;
  cgst: Paise;
  sgst: Paise;
}

/** The tax on one line's taxable value at a rate. */
export function lineTax(taxableValue: Paise, rate: Rate, interState: boolean): LineTax {
  assertPaise(taxableValue, 'taxable value');
  if (taxableValue < 0) throw new RangeError('taxable value must not be negative');
  const bp = rateBasisPoints(rate);
  if (interState) return { igst: share(taxableValue, bp, 10_000), cgst: 0, sgst: 0 };
  const half = share(taxableValue, bp, 20_000);
  return { igst: 0, cgst: half, sgst: half };
}

export interface TaxedLine {
  taxableValue: Paise;
  rate: Rate;
  cess?: Paise;
}

export interface InvoiceTotals {
  taxableValue: Paise;
  igst: Paise;
  cgst: Paise;
  sgst: Paise;
  cess: Paise;
  /** All the tax: IGST + CGST + SGST + cess. */
  tax: Paise;
  /** What the buyer pays: taxable value + tax. */
  invoiceValue: Paise;
}

/** Totals of an invoice: the sum of its lines, each taxed on its own. */
export function invoiceTotals(lines: readonly TaxedLine[], interState: boolean): InvoiceTotals {
  const totals = { taxableValue: 0, igst: 0, cgst: 0, sgst: 0, cess: 0 };
  for (const line of lines) {
    const tax = lineTax(line.taxableValue, line.rate, interState);
    totals.taxableValue += line.taxableValue;
    totals.igst += tax.igst;
    totals.cgst += tax.cgst;
    totals.sgst += tax.sgst;
    totals.cess += line.cess ?? 0;
  }
  const tax = totals.igst + totals.cgst + totals.sgst + totals.cess;
  return { ...totals, tax, invoiceValue: totals.taxableValue + tax };
}

/**
 * The taxable value inside an amount that includes GST ("₹1,18,000 including GST" at 18% is
 * ₹1,00,000), rounded to the paisa.
 */
export function taxableFromInclusive(amount: Paise, rate: Rate): Paise {
  assertPaise(amount, 'amount');
  const bp = rateBasisPoints(rate);
  return Math.floor((amount * 20_000 + 10_000 + bp) / (2 * (10_000 + bp)));
}

/**
 * Where a supply is taxed. For goods delivered to the buyer that's where delivery ends (section 10
 * IGST Act): the buyer's state for a registered buyer unless told otherwise, and the seller's own state
 * for a sale over the counter. `delivery` overrides both.
 */
export function placeOfSupply(input: { supplierState: string; buyerGstin: string | null; delivery?: string | null }) {
  return input.delivery ?? (input.buyerGstin ? input.buyerGstin.slice(0, 2) : input.supplierState);
}

export const isInterState = (supplierState: string, placeOfSupply: string) => supplierState !== placeOfSupply;

/** B2B (table 4A), B2C large (table 5) or B2C others (table 7). */
export const SUPPLY_KINDS = ['b2b', 'b2cl', 'b2cs'] as const;
export type SupplyKind = (typeof SUPPLY_KINDS)[number];

/** An inter-state consumer invoice above this is "B2C large": ₹1 lakh from 1 August 2024, ₹2.5 lakh before. */
export function b2clThreshold(invoiceDate: IsoDate): Paise {
  return invoiceDate >= '2024-08-01' ? 1_00_000_00 : 2_50_000_00;
}

export function supplyKind(input: {
  buyerGstin: string | null;
  interState: boolean;
  invoiceValue: Paise;
  invoiceDate: IsoDate;
}): SupplyKind {
  if (input.buyerGstin) return 'b2b';
  return input.interState && input.invoiceValue > b2clThreshold(input.invoiceDate) ? 'b2cl' : 'b2cs';
}
